import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

// Tour step interface
export interface TourStep {
  id: string;
  target: string; // CSS selector for the target element
  title: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right" | "center";
  highlightPadding?: number;
  spotlightClicks?: boolean; // Allow clicks on the highlighted element
  navigateTo?: string; // Navigate to a specific screen before showing this step (e.g., "103" for Inspection)
  waitForElement?: boolean; // Wait for the target element to appear before showing the step
}

// Predefined tour steps for the application
export const defaultTourSteps: TourStep[] = [
  // ============ WELCOME ============
  {
    id: "welcome",
    target: "body",
    title: "Welcome to Garage 360!",
    content: "Welcome to your complete garage management solution! This tour will guide you through all the features and menus. Click 'Next' to begin or 'Skip' to exit anytime.",
    placement: "center",
  },

  // ============ NAVIGATION MENU OVERVIEW ============
  {
    id: "side-menu",
    target: "[data-tour='side-menu']",
    title: "Navigation Menu",
    content: "This is your main navigation sidebar. It contains all the modules organized by category. Click on any menu item to expand it and see its sub-items. Let's explore each menu section!",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ HOME MENU ============
  {
    id: "menu-home",
    target: "[data-tour='side-menu']",
    title: "Home Dashboard",
    content: "The HOME menu takes you to your dashboard. Here you'll see key metrics at a glance: total employees, customers, vendors, and products. The dashboard also displays important reports and charts for quick business insights.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ GARAGE MENU ============
  {
    id: "menu-garage",
    target: "[data-tour='side-menu']",
    title: "Garage Operations",
    content: "The GARAGE menu is the heart of your operations. It contains:\n\n• Vehicles - Register and manage all customer vehicles\n• Inspection - Record vehicle inspections before service\n• Job Card - Create and track service jobs from start to finish\n\nThis is where you manage the complete vehicle service lifecycle.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ INVENTORY MENU ============
  {
    id: "menu-inventory",
    target: "[data-tour='side-menu']",
    title: "Inventory Management",
    content: "The INVENTORY menu helps you manage all your stock:\n\n• Products - Manage spare parts and items catalog\n• Labour and Other - Track labor charges and service items\n• Stock - Monitor current inventory levels\n• Parts Issue - Issue parts for job cards\n• Parts Return - Return unused parts to stock\n• Parts Request - Request parts from suppliers",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ EMPLOYEE MENU ============
  {
    id: "menu-employee",
    target: "[data-tour='side-menu']",
    title: "Employee Management",
    content: "The EMPLOYEE menu manages your workforce:\n\n• Employees - Add and manage staff profiles\n• Employee Group - Organize employees into teams/departments\n• Sales Agent - Manage sales representatives\n• Users - Control user accounts and system access\n\nAssign roles and permissions from here.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ SALES MENU ============
  {
    id: "menu-sales",
    target: "[data-tour='side-menu']",
    title: "Sales Management",
    content: "The SALES menu handles all sales transactions:\n\n• Customers - Manage customer database and profiles\n• Invoice - Create and track sales invoices\n• Sales Return Vouchers - Process returns and refunds\n\nTrack all your revenue-generating activities here.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ PURCHASES MENU ============
  {
    id: "menu-purchases",
    target: "[data-tour='side-menu']",
    title: "Purchase Management",
    content: "The PURCHASES menu handles procurement:\n\n• Vendors - Manage supplier database\n• Purchase Order - Create orders to suppliers\n• Purchase - Record received purchases\n• Purchase Return Vouchers - Return goods to vendors\n\nKeep track of all your procurement activities.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ ACCOUNTS MENU ============
  {
    id: "menu-accounts",
    target: "[data-tour='side-menu']",
    title: "Accounts & Finance",
    content: "The ACCOUNTS menu manages your finances:\n\n• Cash and Bank - Track cash & bank accounts\n• Charts of Accounts - Define account structure\n• Account Heads & Head Names - Organize ledgers\n• Accounting Years - Manage fiscal periods\n• Ledger Carry Forward - Year-end processing\n• Receipt/Payment Vouchers & Summaries - Track money flow",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ VOUCHERS MENU ============
  {
    id: "menu-vouchers",
    target: "[data-tour='side-menu']",
    title: "Vouchers",
    content: "The VOUCHERS menu handles financial entries:\n\n• Manual Journals - Record adjusting entries\n• Contra Vouchers - Cash/bank transfers\n• Petty Cash Vouchers - Small expense tracking\n• Cash/Bank Payment Vouchers - Record payments\n• Credit Notes - Issue credits to customers\n• Debit Notes - Request credits from vendors",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ MASTERS MENU ============
  {
    id: "menu-masters",
    target: "[data-tour='side-menu']",
    title: "Master Data",
    content: "The MASTERS menu contains configuration data:\n\n• Vehicle Make - Define car brands (Toyota, Honda, etc.)\n• Vehicle Type - Define vehicle categories (Sedan, SUV, etc.)\n• Lookups - System dropdown values\n• Branches - Manage multi-location setup\n\nSet up your business foundation here.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ REPORTS MENU ============
  {
    id: "menu-reports",
    target: "[data-tour='side-menu']",
    title: "Reports & Analytics",
    content: "The REPORTS menu provides business intelligence. Access various reports to analyze:\n\n• Sales performance\n• Inventory movement\n• Financial statements\n• Job card statistics\n• Customer insights\n\nMake data-driven decisions with comprehensive reporting.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ HEADER CONTROLS ============
  {
    id: "dark-mode",
    target: "[data-tour='dark-mode']",
    title: "Dark Mode Toggle",
    content: "Switch between light and dark themes for comfortable viewing. Dark mode is easier on the eyes, especially in low-light conditions. Your preference is automatically saved.",
    placement: "bottom",
    highlightPadding: 4,
  },
  {
    id: "branch-selector",
    target: "[data-tour='branch-selector']",
    title: "Branch Selector",
    content: "If your business has multiple locations, use this to switch between branches. All data (invoices, stock, employees) will be filtered based on the selected branch.",
    placement: "bottom",
    highlightPadding: 4,
  },
  {
    id: "profile",
    target: "[data-tour='profile']",
    title: "Your Profile",
    content: "Click here to access your profile. You can:\n\n• View your account details\n• Change the theme color (Green, Blue, Purple, Orange)\n• Start this tour again anytime\n• Logout from the system",
    placement: "left",
    highlightPadding: 4,
  },

  // ============ NAVIGATING TO INSPECTION PAGE ============
  {
    id: "navigate-to-inspection",
    target: "body",
    title: "Let's Explore Data Tables",
    content: "Now let's see the data table features in action! We'll navigate to the INSPECTION screen - a perfect example that showcases all table features including workflow progress tracking.\n\nThe Inspection screen tracks vehicle inspections from creation to invoicing.",
    placement: "center",
    navigateTo: "103", // Navigate to Inspection screen
  },

  // ============ DATA TABLE OVERVIEW - ON INSPECTION PAGE ============
  {
    id: "data-table-overview",
    target: "[data-tour='data-table']",
    title: "Inspection Data Table",
    content: "This is the Inspection data table. It displays all vehicle inspections with powerful features:\n\n• Sortable columns\n• Search & filtering\n• Row selection\n• Workflow tracking\n• Action buttons\n\nLet's explore each feature in detail!",
    placement: "top",
    highlightPadding: 8,
    navigateTo: "103",
    waitForElement: true,
  },

  // ============ TABLE HEADER - COLUMN HEADERS ============
  {
    id: "table-headers",
    target: "[data-tour='table-header']",
    title: "Table Headers & Sorting",
    content: "The table header row shows all column names:\n\n• Click any column header to SORT data (ascending/descending)\n• Click again to reverse the sort order\n• A sort indicator arrow shows the current sort direction\n• Right-click a header for more options like Hide or Pin",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ SEARCH BOX ============
  {
    id: "table-search",
    target: "[data-tour='table-search']",
    title: "Search & Filter",
    content: "Use the search box to quickly find records:\n\n• Type to search across all visible columns\n• Results update instantly as you type\n• Clear the search to show all records again\n• Combine with column filters for precise results",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ VIEW SWITCHER ============
  {
    id: "view-switcher",
    target: "[data-tour='view-switcher']",
    title: "View Switcher: Table vs Card",
    content: "Toggle between two display modes:\n\n• TABLE VIEW - Traditional rows and columns format, best for comparing multiple records side by side\n\n• CARD VIEW - Visual cards showing key information prominently, perfect for quick scanning and mobile devices\n\nYour preference is automatically saved!",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ TABLE VIEW MODE SWITCHER ============
  {
    id: "table-view-mode",
    target: "[data-tour='table-view-mode']",
    title: "Progress vs Standard View",
    content: "When workflow actions exist, you can switch between:\n\n• PROGRESS VIEW - Shows the workflow column with status badges, progress bars (0-100%), and the next action button\n\n• STANDARD VIEW - Hides workflow details for a cleaner, data-focused table\n\nUse Standard view when you just need to see the data!",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ ADD NEW RECORD BUTTON ============
  {
    id: "add-button",
    target: "[data-tour='add-button']",
    title: "Add New Record",
    content: "Click this button to create a new inspection record. It opens a form where you can enter:\n\n• Vehicle information\n• Customer details\n• Inspection notes\n• Attach photos/documents\n\nAfter saving, the new record appears in the table.",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ TABLE ROW SELECTION ============
  {
    id: "table-row",
    target: "[data-tour='data-table']",
    title: "Row Selection & Actions",
    content: "Click any row to SELECT it (highlighted in blue). Selected rows can have actions performed on them:\n\n• VIEW - Open record details\n• EDIT - Modify the record\n• DELETE - Remove the record\n• PRINT/PDF - Generate printable document\n\nAction buttons appear in the Actions column.",
    placement: "top",
    highlightPadding: 8,
    navigateTo: "103",
  },

  // ============ WORKFLOW PROGRESS COLUMN ============
  {
    id: "workflow-column",
    target: "[data-tour='workflow-column']",
    title: "Workflow Progress Column",
    content: "This column shows the workflow status of each inspection:\n\n• STATUS BADGE - Current stage (Open, In Progress, Completed, etc.)\n• PROGRESS BAR - Visual percentage of completion\n• NEXT ACTION BUTTON - Click to advance to the next stage\n\nColors indicate progress:\n• Gray = Pending\n• Blue = In Progress\n• Green = Completed",
    placement: "left",
    highlightPadding: 8,
    navigateTo: "103",
  },

  // ============ WORKFLOW STAGES EXPLAINED ============
  {
    id: "workflow-stages",
    target: "[data-tour='data-table']",
    title: "Workflow Stages",
    content: "Inspections move through these workflow stages:\n\n1. OPEN - Newly created inspection\n2. IN PROGRESS - Work has started\n3. COMPLETED - Inspection finished\n4. JOB CARD - Converted to job card\n5. INVOICED - Invoice generated\n6. CLOSED - Fully completed\n\nClick the workflow button to advance records!",
    placement: "top",
    highlightPadding: 8,
    navigateTo: "103",
  },

  // ============ ACTIONS COLUMN ============
  {
    id: "actions-column",
    target: "[data-tour='actions-column']",
    title: "Actions Column",
    content: "The rightmost column contains action buttons:\n\n• EDIT (Pencil icon) - Modify the record\n• DELETE (Trash icon) - Remove the record\n• VIEW (Eye icon) - View details in read-only mode\n• PRINT (Printer icon) - Generate PDF\n• TIMELINE (Clock icon) - View activity history\n\nHover over icons to see tooltips!",
    placement: "left",
    highlightPadding: 8,
    navigateTo: "103",
  },

  // ============ COLUMN CUSTOMIZATION ============
  {
    id: "column-customization",
    target: "[data-tour='column-menu']",
    title: "Column Customization",
    content: "Click the column menu icon to customize columns:\n\n• SHOW/HIDE COLUMNS - Toggle visibility of any column\n• PIN COLUMNS - Keep important columns fixed while scrolling\n• RESET - Restore default column settings\n\nYour preferences are saved automatically per table!",
    placement: "bottom",
    highlightPadding: 4,
    navigateTo: "103",
  },

  // ============ INFINITE SCROLL / PAGINATION ============
  {
    id: "infinite-scroll",
    target: "[data-tour='data-table']",
    title: "Loading More Data",
    content: "Tables use infinite scroll for large datasets:\n\n• Scroll down to automatically load more records\n• A loading indicator appears while fetching\n• No need to click 'Next Page' buttons!\n• Works smoothly with thousands of records\n\nThe table remembers your scroll position.",
    placement: "top",
    highlightPadding: 8,
    navigateTo: "103",
  },

  // ============ CARD VIEW FEATURES ============
  {
    id: "card-view-features",
    target: "body",
    title: "Card View Features",
    content: "Switch to Card View for a different perspective:\n\n• Each record displayed as a visual card\n• Key information shown prominently (VIN, Customer, Vehicle)\n• Status badges and progress bars on each card\n• Action buttons for quick operations\n• Search functionality to filter cards\n• Same infinite scroll behavior\n\nIdeal for mobile devices and quick scanning!",
    placement: "center",
    navigateTo: "103",
  },

  // ============ TABLE CUSTOMIZATION SUMMARY ============
  {
    id: "table-customization",
    target: "body",
    title: "Your Preferences Are Saved",
    content: "All your table customizations are automatically saved:\n\n• View mode (Table or Card)\n• Table view mode (Progress or Standard)\n• Column visibility settings\n• Column pinning preferences\n• Sort order\n\nWhen you return to this screen, everything will be exactly as you left it!",
    placement: "center",
    navigateTo: "103",
  },

  // ============ CHATBOT ============
  {
    id: "chatbot",
    target: "[data-tour='chatbot']",
    title: "AI Assistant",
    content: "Need help? Click the chat icon to open the AI assistant. You can:\n\n• Ask questions about features\n• Get help navigating the app\n• Request guidance on tasks\n• Find information quickly\n\nYour intelligent helper is always available!",
    placement: "left",
    highlightPadding: 4,
  },

  // ============ KEYBOARD SHORTCUTS ============
  {
    id: "keyboard-shortcuts",
    target: "body",
    title: "Keyboard Shortcuts",
    content: "Work faster with keyboard shortcuts:\n\n• Ctrl+S - Save current form\n• Ctrl+Shift+S - Save and close\n• Ctrl+E - Edit record\n• Ctrl+D - Delete record\n• Ctrl+P - Print/PDF\n• Ctrl+Shift+D - Toggle dark mode\n• Ctrl+B - Toggle sidebar\n• Escape - Close modals/go back",
    placement: "center",
  },

  // ============ SETTINGS ============
  {
    id: "settings",
    target: "[data-tour='side-menu']",
    title: "Settings",
    content: "At the bottom of the sidebar, you'll find SETTINGS. Configure:\n\n• Company details\n• Access rights and permissions\n• System preferences\n• User management\n\nAdministrators can control all aspects of the system.",
    placement: "right",
    highlightPadding: 8,
  },

  // ============ COMPLETION ============
  {
    id: "complete",
    target: "body",
    title: "Tour Complete!",
    content: "Congratulations! You've completed the Garage 360 tour.\n\nKey takeaways:\n• Use the sidebar to navigate between modules\n• Switch between Table and Card views\n• Track work progress with workflow features\n• Customize tables to your preference\n\nRestart this tour anytime from your profile. Happy working!",
    placement: "center",
  },
];

// Navigation callback type
type NavigationCallback = (screenId: string) => void;

// Tour context interface
interface TourContextType {
  isOpen: boolean;
  currentStep: number;
  steps: TourStep[];
  startTour: (customSteps?: TourStep[]) => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  hasCompletedTour: boolean;
  markTourComplete: () => void;
  resetTourStatus: () => void;
  setNavigationCallback: (callback: NavigationCallback) => void;
  currentStepData: TourStep | undefined;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

// Storage key for tour completion status
const TOUR_COMPLETED_KEY = "garage360-tour-completed";

const getTourCompletedStatus = (): boolean => {
  try {
    const stored = localStorage.getItem(TOUR_COMPLETED_KEY);
    return stored === "true";
  } catch {
    return false;
  }
};

const saveTourCompletedStatus = (completed: boolean): void => {
  try {
    localStorage.setItem(TOUR_COMPLETED_KEY, String(completed));
  } catch {
    // Ignore storage errors
  }
};

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>(defaultTourSteps);
  const [hasCompletedTour, setHasCompletedTour] = useState<boolean>(() => getTourCompletedStatus());
  const [navigationCallback, setNavigationCallbackState] = useState<NavigationCallback | null>(null);
  
  // Current step data
  const currentStepData = steps[currentStep];
  
  // Set the navigation callback
  const setNavigationCallback = useCallback((callback: NavigationCallback) => {
    setNavigationCallbackState(() => callback);
  }, []);

  const startTour = useCallback((customSteps?: TourStep[]) => {
    if (customSteps) {
      setSteps(customSteps);
    } else {
      setSteps(defaultTourSteps);
    }
    setCurrentStep(0);
    setIsOpen(true);
  }, []);

  const endTour = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextStepData = steps[nextStepIndex];
      
      // If next step requires navigation, trigger it
      if (nextStepData?.navigateTo && navigationCallback) {
        navigationCallback(nextStepData.navigateTo);
        // Add a small delay for navigation to complete
        setTimeout(() => {
          setCurrentStep(nextStepIndex);
        }, 500);
      } else {
        setCurrentStep(nextStepIndex);
      }
    } else {
      // Last step reached, mark tour as complete
      markTourComplete();
      endTour();
    }
  }, [currentStep, steps, navigationCallback]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const prevStepIndex = currentStep - 1;
      const prevStepData = steps[prevStepIndex];
      
      // If previous step requires navigation, trigger it
      if (prevStepData?.navigateTo && navigationCallback) {
        navigationCallback(prevStepData.navigateTo);
        setTimeout(() => {
          setCurrentStep(prevStepIndex);
        }, 500);
      } else {
        setCurrentStep(prevStepIndex);
      }
    }
  }, [currentStep, steps, navigationCallback]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStep(index);
    }
  }, [steps.length]);

  const markTourComplete = useCallback(() => {
    setHasCompletedTour(true);
    saveTourCompletedStatus(true);
  }, []);

  const resetTourStatus = useCallback(() => {
    setHasCompletedTour(false);
    saveTourCompletedStatus(false);
  }, []);

  return (
    <TourContext.Provider
      value={{
        isOpen,
        currentStep,
        steps,
        startTour,
        endTour,
        nextStep,
        prevStep,
        goToStep,
        hasCompletedTour,
        markTourComplete,
        resetTourStatus,
        setNavigationCallback,
        currentStepData,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
